import { type RefObject, useEffect } from 'react';

type Ref = RefObject<Element | null>;

/**
 * A type predicate rather than `instanceof Element`: that global doesn't exist during SSR (see the
 * note in useScrollContainer).
 */
function isElement(target: EventTarget | null): target is Element {
	return target !== null && 'parentElement' in target;
}

function findScrollerItem(scroller: Element, target: EventTarget | null): Element | null {
	let node = isElement(target) ? target : null;
	while (node && node.parentElement !== scroller) {
		node = node.parentElement;
	}
	return node;
}

/**
 * Keeps the carousel item holding focus fully visible.
 *
 * A card's focusable control is often a visually-hidden 1px box (the SelectionCard radios in the
 * flyer download modal), so the browser's native scroll-into-view only reveals that box and leaves
 * a partially-shown card partially shown. Aligning the item's own start edge with the scroller's
 * reveals all of it and lands on its scroll-snap position.
 */
export function useKeepFocusedItemInView(scrollContainerRef: Ref): void {
	useEffect(() => {
		const scroller = scrollContainerRef.current;
		if (!scroller) return undefined;

		const handleFocusIn = (event: Event) => {
			const item = findScrollerItem(scroller, event.target);
			if (!item) return;

			const scrollerRect = scroller.getBoundingClientRect();
			const itemRect = item.getBoundingClientRect();
			if (itemRect.left >= scrollerRect.left && itemRect.right <= scrollerRect.right) return;

			// Absolute, not scrollBy: focusing already started the browser's own smooth scroll, and a
			// relative amount would compound with whatever it has animated so far. No explicit
			// `behavior` -- that would override the scroller's `motion-safe:scroll-smooth`, animating
			// even for someone who asked for reduced motion.
			scroller.scrollTo({ left: scroller.scrollLeft + (itemRect.left - scrollerRect.left) });
		};

		scroller.addEventListener('focusin', handleFocusIn);

		return () => {
			scroller.removeEventListener('focusin', handleFocusIn);
		};
	}, [scrollContainerRef]);
}
